import type { PaymentProvider } from '@mfw/common';
import { InstrumentStatus, WALLET_SETUP_SESSION_PARAM } from '@mfw/common';
import { useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import React from 'react';

import type {
  ApiPaymentInstrument,
  WalletOwnerArgs,
} from '../../api/types/payments.types';

import { startProviderRedirect } from './startProviderRedirect';
import { useSetupWalletInstrument } from '../../hooks/wallet';
import { useGetUrlParams } from '../../hooks/useGetUrlParams';
import { resolveWalletSetup } from '../../api/wallet.api';
import { usePollUntil } from '../../hooks/usePollUntil';
import { buildReturnUrl } from '../../utils';
import { useSnackbar } from '../../context';
import { queryKeys } from '../../queryKeys';
import messages from './messages';

/** An intent older than 30 min is ignored — a stale redirect must not purchase. */
const INTENT_TTL_MS = 30 * 60 * 1000;

function intentStorageKey(scope: string) {
  return `mfw-pending-purchase:${scope}`;
}

type StoredIntent<TIntent> = {
  expiresAt: number;
  intent: TIntent;
  /**
   * Processor the flow requires, captured at redirect time (R-AUD-91): the
   * hosted setup is a full-page redirect, so the `provider` option (derived
   * from React state like the selected plan/event) does NOT survive into the
   * return mount — the poll must read it from here, not from the prop.
   */
  provider?: PaymentProvider;
};

export interface PurchaseWithCardSetupOptions<TIntent> {
  /** Executes the purchase once a usable card arrives (the intent is already deleted). */
  onResume: (intent: TIntent) => void;
  /** Reserves the caller's global owner before return polling starts. */
  onResumeStart?: (intent: TIntent) => void;
  /** Releases that reservation when return polling will not execute the purchase. */
  onResumeEnd?: (intent: TIntent) => void;
  /** Releases the caller's pending state when hosted setup creation fails. */
  onSetupError?: () => void;
  /** Wallet owner the card is added to (org or user). */
  owner: WalletOwnerArgs;
  /**
   * Processor of the flow being paid (R-AUD-91): the card added here must be
   * chargeable by the purchase that follows — premium passes the plan's
   * provider, booking the event's. Omit when the BE owner rule already
   * matches the flow (org billing: the org's plan provider).
   */
  provider?: PaymentProvider;
  /** FE return route from hosted setup (incl. `tab` query if present). */
  returnRoute: string;
  /** Unique flow key (e.g. `org-packages:<orgId>`) — isolates intents. */
  scope: string;
}

/**
 * "2-in-1" purchase without a saved card (R-AUD-65): clicking "Buy" stores
 * the intent in sessionStorage and navigates to the existing hosted card
 * entry (Stripe Checkout mode=setup); on return (`?setup=success&purchase=1`)
 * self-heal resolve + poll the wallet until a usable card arrives, then the
 * purchase is executed AUTOMATICALLY via `onResume`. Canceling on the hosted
 * side deletes the intent. Closing the tab after entry = the card is saved
 * but the purchase isn't executed (the next click purchases directly) — a
 * knowingly accepted degradation.
 */
export const usePurchaseWithCardSetup = <TIntent>({
  onResume,
  onResumeEnd,
  onResumeStart,
  onSetupError,
  owner,
  provider,
  returnRoute,
  scope,
}: PurchaseWithCardSetupOptions<TIntent>) => {
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [params, setSearchParams] = useGetUrlParams();

  const { isLoading: isPreparing, mutate: setupInstrument } =
    useSetupWalletInstrument();

  // callback identity does not restart the resume effect
  const onResumeRef = React.useRef(onResume);
  onResumeRef.current = onResume;
  const onResumeEndRef = React.useRef(onResumeEnd);
  onResumeEndRef.current = onResumeEnd;
  const onResumeStartRef = React.useRef(onResumeStart);
  onResumeStartRef.current = onResumeStart;
  const reservedResumeIntentRef = React.useRef<TIntent | null>(null);

  const storageKey = intentStorageKey(scope);

  const readStored = React.useCallback((): StoredIntent<TIntent> | null => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (!raw) return null;
      const stored = JSON.parse(raw) as StoredIntent<TIntent>;
      if (!stored.expiresAt || stored.expiresAt < Date.now()) {
        sessionStorage.removeItem(storageKey);
        return null;
      }
      return stored;
    } catch {
      return null;
    }
  }, [storageKey]);

  const readIntent = React.useCallback(
    () => readStored()?.intent ?? null,
    [readStored],
  );

  /** Strips setup/purchase markers from the URL (other params remain). */
  const clearMarkers = React.useCallback(() => {
    const rest: Record<string, string> = {};
    for (const [key, value] of Object.entries(params)) {
      if (
        key !== 'setup' &&
        key !== WALLET_SETUP_SESSION_PARAM &&
        key !== 'purchase' &&
        value !== undefined
      ) {
        rest[key] = value;
      }
    }
    setSearchParams(rest, { replace: true });
  }, [params, setSearchParams]);

  const isPurchaseReturn = params.purchase === '1';
  const setupMarker = isPurchaseReturn ? params.setup : undefined;
  const hasIntent = isPurchaseReturn && readIntent() !== null;

  // Reserve the persisted flow before the poll can yield to user actions.
  // StrictMode replays effects, so the reservation callback must be emitted
  // once per mounted return leg; the global owner is independently atomic.
  const resumeStartedRef = React.useRef(false);
  React.useEffect(() => {
    if (setupMarker !== 'success' || !hasIntent) return;
    if (resumeStartedRef.current) return;
    const intent = readIntent();
    if (intent === null) return;
    resumeStartedRef.current = true;
    reservedResumeIntentRef.current = intent;
    onResumeStartRef.current?.(intent);
  }, [hasIntent, readIntent, setupMarker]);

  // Self-heal (R-AUD-53): the success URL carries the session id — BE
  // immediately fetches the session outcome instead of waiting for the
  // webhook; the poll below remains a fallback.
  const resolveAttemptedRef = React.useRef(false);
  React.useEffect(() => {
    const sessionId = params[WALLET_SETUP_SESSION_PARAM];
    if (setupMarker !== 'success' || !sessionId || !hasIntent) return;
    if (resolveAttemptedRef.current) return;
    resolveAttemptedRef.current = true;
    resolveWalletSetup({ ...owner, sessionId })
      .then(() => {
        const { queryFn, queryKey } = queryKeys.wallet.fetchInstruments(owner);
        return queryClient.fetchQuery({ queryFn, queryKey, staleTime: 0 });
      })
      .catch(() => {
        // poll fallback takes over (the webhook may have arrived after all)
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setupMarker, params[WALLET_SETUP_SESSION_PARAM], hasIntent]);

  const { status: pollStatus } = usePollUntil({
    enabled: setupMarker === 'success' && hasIntent,
    isDone: (fresh: ApiPaymentInstrument[]) => {
      // a pre-existing card of another processor must NOT end the poll — the
      // resumed purchase can only charge the provider stored at redirect time
      const required = readStored()?.provider ?? provider;
      return fresh.some(
        (instrument) =>
          instrument.status === InstrumentStatus.ACTIVE &&
          instrument.mitEligible &&
          (!required || instrument.provider === required),
      );
    },
    poll: () => {
      const { queryFn, queryKey } = queryKeys.wallet.fetchInstruments(owner);
      return queryClient.fetchQuery({ queryFn, queryKey, staleTime: 0 });
    },
  });

  React.useEffect(() => {
    if (pollStatus !== 'done' && pollStatus !== 'timeout') return;
    if (pollStatus === 'timeout') {
      // the intent remains stored, but clearing the URL markers below makes
      // it unreachable — after a timeout the user re-clicks Buy, which then
      // purchases directly once the card has landed (accepted degradation)
      enqueueSnackbar({
        message: formatMessage(messages.purchaseResumeTimeout),
        variant: 'info',
      });
      const reserved = reservedResumeIntentRef.current;
      reservedResumeIntentRef.current = null;
      if (reserved !== null) onResumeEndRef.current?.(reserved);
      clearMarkers();
      return;
    }
    const intent = readIntent();
    // the intent is deleted BEFORE execution — a duplicate mount/refresh must not purchase twice
    sessionStorage.removeItem(storageKey);
    clearMarkers();
    if (intent !== null) {
      reservedResumeIntentRef.current = null;
      onResumeRef.current(intent);
    } else {
      const reserved = reservedResumeIntentRef.current;
      reservedResumeIntentRef.current = null;
      if (reserved !== null) onResumeEndRef.current?.(reserved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollStatus]);

  React.useEffect(() => {
    if (setupMarker !== 'cancel') return;
    const intent = reservedResumeIntentRef.current ?? readIntent();
    reservedResumeIntentRef.current = null;
    sessionStorage.removeItem(storageKey);
    if (intent !== null) onResumeEndRef.current?.(intent);
    enqueueSnackbar({
      message: formatMessage(messages.purchaseResumeCanceled),
      variant: 'info',
    });
    clearMarkers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setupMarker]);

  /**
   * Stores the intent and navigates to hosted card entry; the purchase is
   * executed on return. Call ONLY when a usable card does not exist.
   */
  const beginCardSetupThenPurchase = (intent: TIntent) => {
    const stored: StoredIntent<TIntent> = {
      expiresAt: Date.now() + INTENT_TTL_MS,
      intent,
      provider,
    };
    sessionStorage.setItem(storageKey, JSON.stringify(stored));
    setupInstrument(
      {
        ...owner,
        cancelUrl: buildReturnUrl(returnRoute, {
          purchase: '1',
          setup: 'cancel',
        }),
        provider,
        successUrl: buildReturnUrl(returnRoute, {
          purchase: '1',
          setup: 'success',
        }),
      },
      {
        onError: () => {
          sessionStorage.removeItem(storageKey);
          onSetupError?.();
        },
        onSuccess: (result) => {
          if (!result) return;
          startProviderRedirect(result);
        },
      },
    );
  };

  return {
    beginCardSetupThenPurchase,
    /** Setup mutation in progress (before the redirect). */
    isPreparing,
    /** Return from hosted setup: the card is confirmed before the purchase. */
    isResuming: pollStatus === 'polling',
  };
};
